import React from "react";
import { cn } from "@/lib/utils";

export const seg = "inline-flex rounded-full bg-black/[0.045] p-0.5";
export const segBtn = (on, tone) => cn(
  "inline-flex h-7 min-w-[28px] items-center justify-center rounded-full px-2 text-[12px] font-medium transition-[background-color,color]",
  on ? cn("bg-white shadow-[0_1px_2px_hsl(212_40%_16%/0.10)]", tone === "warn" ? "text-[hsl(var(--v-assumed))]" : tone === "ok" ? "text-[hsl(var(--v-ok))]" : "text-foreground") : "text-muted-foreground hover:text-foreground",
);
export const field = "h-8 w-full rounded-md border border-black/[0.1] bg-white px-2 text-[12.5px] outline-none transition-[border-color,box-shadow] focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)]";
export const area = cn(field, "h-auto min-h-[34px] resize-y py-1.5 leading-[1.4]");

/** Score 1–5 or empty («–» = ikke definert, never 0). */
export function ScorePick({ value, onChange, label, testId, disabled }) {
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testId}>
      {label ? <span className="w-3 text-[11px] font-medium text-muted-foreground">{label}</span> : null}
      <div className={seg} role="radiogroup" aria-label={label}>
        <button type="button" role="radio" aria-checked={value == null} disabled={disabled} onClick={() => onChange(null)} className={segBtn(value == null)} title="Ikke definert" data-testid={testId ? `${testId}-none` : undefined}>–</button>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={value === n} disabled={disabled} onClick={() => onChange(n)} className={segBtn(value === n)} data-testid={testId ? `${testId}-${n}` : undefined}>{n}</button>
        ))}
      </div>
    </div>
  );
}

const BAND_CLS = { lav: "v-ok", middels: "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]", hoy: "bg-[hsl(var(--v-blocked-bg))] text-[hsl(var(--v-blocked))]" };
const CLASS_CLS = { KRITISK: "bg-[hsl(var(--v-blocked-bg))] text-[hsl(var(--v-blocked))]", BETYDELIG: "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]", AKSEPTABEL: "v-ok", "IKKE DEF": "bg-black/[0.05] text-muted-foreground" };

/** Computed risk = S × K with the source colour band (or the extended-analysis class). Empty factors read «ikke definert». */
export function RiskValue({ score, extended, testId }) {
  const v = score?.value;
  if (v == null) return <span className="inline-flex h-6 items-center rounded-full bg-black/[0.05] px-2 text-[11.5px] italic text-muted-foreground" data-testid={testId} data-value="undefined">ikke definert</span>;
  if (extended) {
    const c = score.class || "IKKE DEF";
    return <span className={cn("inline-flex h-6 items-center gap-1.5 rounded-full px-2 text-[11.5px] font-medium", CLASS_CLS[c])} data-testid={testId} data-value={v} data-class={c}><span className="font-mono tabular-nums">{v}</span> {c}</span>;
  }
  const b = score.band;
  return <span className={cn("inline-flex h-6 items-center gap-1.5 rounded-full px-2 text-[11.5px] font-medium", b ? BAND_CLS[b.key] : "")} data-testid={testId} data-value={v} data-band={b?.key} title={b ? `${b.label} – ${b.action}` : undefined}><span className="font-mono tabular-nums">{v}</span> {b?.label}</span>;
}

const STATE = {
  vurdert: ["Vurdert", "v-ok"],
  mangler_score: ["Mangler score", "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]"],
  ikke_relevant: ["Ikke relevant", "bg-black/[0.05] text-muted-foreground"],
  mangler_begrunnelse: ["Begrunnelse mangler", "bg-[hsl(var(--v-blocked-bg))] text-[hsl(var(--v-blocked))]"],
  ikke_tatt_stilling: ["Ikke tatt stilling", "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]"],
};
export function StatePill({ state, testId }) {
  const [label, cls] = STATE[state] || [state, ""];
  return <span className={cn("inline-flex h-5 items-center rounded-full px-1.5 text-[10.5px] font-medium", cls)} data-testid={testId} data-state={state}>{label}</span>;
}

export function YesNo({ value, onChange, testId, options = [["ja", "Ja"], ["nei", "Nei"]] }) {
  return (
    <div className={seg} role="radiogroup" data-testid={testId}>
      {options.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(value === k ? null : k)} className={segBtn(value === k)} data-testid={testId ? `${testId}-${k}` : undefined}>{l}</button>)}
    </div>
  );
}
